"use client";

import * as React from "react";
import { format, parse } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface DatePickerProps {
  id?: string;
  label?: string;
  selected?: Date;
  onSelect: (date: Date | undefined) => void;
  className?: string;
  placeholder?: string;
  fromYear?: number;
  toYear?: number;
}

export function DatePickerComponent({
  id,
  label,
  selected,
  onSelect,
  className,
  placeholder = "dd/mm/aaaa",
  fromYear,
  toYear,
}: Readonly<DatePickerProps>) {
  const currentYear = new Date().getFullYear();

  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState<string>("");
  const [month, setMonth] = React.useState<Date | undefined>(selected);

  // Sincroniza o valor do input com a data selecionada (prop)
  React.useEffect(() => {
    if (selected) {
      setInputValue(format(selected, "dd/MM/yyyy", { locale: ptBR }));
    } else {
      setInputValue("");
    }
    setMonth(selected);
  }, [selected]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    const rawValue = value.replace(/\D/g, "");
    let maskedValue = rawValue;
    if (rawValue.length > 2) {
      maskedValue = `${rawValue.slice(0, 2)}/${rawValue.slice(2)}`;
    }
    if (rawValue.length > 4) {
      maskedValue = `${maskedValue.slice(0, 5)}/${rawValue.slice(4, 8)}`;
    }

    setInputValue(maskedValue);

    if (maskedValue.length === 10) {
      try {
        const parsedDate = parse(maskedValue, "dd/MM/yyyy", new Date(), {
          locale: ptBR,
        });
        if (!isNaN(parsedDate.getTime())) {
          if (selected?.getTime() !== parsedDate.getTime()) {
            onSelect(parsedDate);
          }
        }
      } catch {
        // Ignora erros de parsing durante a digitação
      }
    }
  };

  const handleBlur = () => {
    // Valida e formata a data ao sair do campo
    if (inputValue) {
      try {
        const parsedDate = parse(inputValue, "dd/MM/yyyy", new Date(), {
          locale: ptBR,
        });
        if (!isNaN(parsedDate.getTime())) {
          onSelect(parsedDate);
          setInputValue(format(parsedDate, "dd/MM/yyyy", { locale: ptBR }));
        } else {
          throw new Error("Data inválida");
        }
      } catch {
        // Se for inválida, reverte para a última data válida ou limpa o campo
        if (selected) {
          setInputValue(format(selected, "dd/MM/yyyy", { locale: ptBR }));
        } else {
          setInputValue("");
        }
      }
    } else {
      // Se o campo estiver vazio, limpa a seleção
      onSelect(undefined);
    }
  };

  const handleDateSelect = (date: Date | undefined) => {
    setOpen(false);
    if (date) {
      onSelect(date);
    }
  };

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {label && (
        <label htmlFor={id} className="px-1 text-sm font-medium">
          {label}
        </label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <div className="relative w-full max-w-[240px]">
          <Input
            id={id}
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleBlur}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setOpen(true);
              }
            }}
            placeholder={placeholder}
            className="pr-10"
          />
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 rounded-full text-muted-foreground hover:bg-muted"
              aria-label="Abrir calendário"
            >
              <CalendarIcon className="h-5 w-5" />
            </Button>
          </PopoverTrigger>
        </div>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selected}
            onSelect={handleDateSelect}
            month={month}
            onMonthChange={setMonth}
            autoFocus
            locale={ptBR}
            captionLayout="dropdown"
            fromYear={fromYear ?? currentYear - 100}
            toYear={toYear ?? currentYear + 100}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
